import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, MapPin, Calendar, Ruler, Shield, User, Mail, Phone, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { SEO } from '@/components/SEO';
import { getExternalListingHref, getListingMailtoHref, getPhoneHref, getSellerContact } from '@/lib/listingContact';
import { formatGender, formatHypoallergenic } from '@/lib/listingAttributes';

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    api.getListing(id)
      .then(r => setListing(r.data))
      .catch(() => navigate('/dogs-for-sale'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-pulse">
      <div className="h-8 bg-gray-200 rounded w-32 mb-6" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="aspect-square bg-gray-200 rounded-3xl" />
        <div className="space-y-4"><div className="h-8 bg-gray-200 rounded w-3/4" /><div className="h-4 bg-gray-200 rounded w-1/2" /></div>
      </div>
    </div>
  );

  if (!listing) return null;

  const images = listing.images?.length > 0
    ? listing.images.map(p => api.getFileUrl(p))
    : ['https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800&h=800&fit=crop'];
  const { email, phone } = getSellerContact(listing);
  const emailHref = getListingMailtoHref(listing);
  const phoneHref = getPhoneHref(phone);
  const externalHref = getExternalListingHref(listing.external_url);
  const sourceLabel = listing.source_name || 'original website';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in" data-testid="listing-detail-page">
      <SEO
        title={`${listing.title} - ${listing.breed} for Sale`}
        description={`${listing.breed}, ${listing.age}, ${listing.size} size. Located in ${listing.location}. Price: $${listing.price?.toLocaleString()}. ${listing.description?.slice(0, 100)}...`}
        keywords={`${listing.breed} for sale, ${listing.breed} puppy, dogs for sale ${listing.location}, buy ${listing.breed}`}
        type="product"
      />
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#5C636A] hover:text-[#1A1D20] mb-6 transition-colors" data-testid="back-button">
        <ArrowLeft className="w-4 h-4" /> Back to listings
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Image Gallery */}
        <div className="space-y-3" data-testid="image-gallery">
          <div className="relative aspect-square rounded-3xl overflow-hidden bg-gray-100">
            <img src={images[activeImage]} alt={listing.title} className="w-full h-full object-cover" />
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setActiveImage(i => i > 0 ? i - 1 : images.length - 1)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
                  data-testid="gallery-prev"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setActiveImage(i => i < images.length - 1 ? i + 1 : 0)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
                  data-testid="gallery-next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`w-16 h-16 rounded-xl overflow-hidden shrink-0 ring-2 transition-all ${
                    i === activeImage ? 'ring-[#4A90E2] ring-offset-2' : 'ring-transparent'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="space-y-6">
          <div>
            <div className="flex flex-wrap gap-2 mb-3">
              {listing.vaccination_status === 'vaccinated' && (
                <Badge className="bg-[#7ED321] text-white border-0 rounded-full text-xs font-semibold px-3 py-1">
                  <Shield className="w-3 h-3 mr-1" /> Vaccinated
                </Badge>
              )}
              <Badge className="bg-blue-50 text-[#4A90E2] border-0 rounded-full text-xs font-semibold px-3 py-1">
                {listing.size}
              </Badge>
              {listing.hypoallergenic === 'yes' && (
                <Badge className="bg-orange-50 text-[#F5A623] border-0 rounded-full text-xs font-semibold px-3 py-1">
                  Hypoallergenic
                </Badge>
              )}
              <Badge className="bg-gray-100 text-[#5C636A] border-0 rounded-full text-xs font-semibold px-3 py-1">
                {listing.status}
              </Badge>
            </div>
            <h1 className="font-['Nunito'] font-extrabold text-3xl sm:text-4xl text-[#1A1D20] tracking-tight" data-testid="listing-title">
              {listing.title}
            </h1>
            <p className="text-lg font-medium text-[#5C636A] mt-2">{listing.breed}</p>
          </div>

          <p className="font-['Nunito'] font-extrabold text-4xl text-[#F5A623]" data-testid="listing-price">
            ${listing.price?.toLocaleString()}
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-2xl p-4">
              <div className="flex items-center gap-2 text-[#5C636A] mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-xs font-medium">Age</span>
              </div>
              <p className="font-semibold text-[#1A1D20]">{listing.age}</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-4">
              <div className="flex items-center gap-2 text-[#5C636A] mb-1">
                <MapPin className="w-4 h-4" />
                <span className="text-xs font-medium">Location</span>
              </div>
              <p className="font-semibold text-[#1A1D20]">{listing.location}</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-4">
              <div className="flex items-center gap-2 text-[#5C636A] mb-1">
                <Ruler className="w-4 h-4" />
                <span className="text-xs font-medium">Size</span>
              </div>
              <p className="font-semibold text-[#1A1D20] capitalize">{listing.size}</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-4">
              <div className="flex items-center gap-2 text-[#5C636A] mb-1">
                <User className="w-4 h-4" />
                <span className="text-xs font-medium">Gender</span>
              </div>
              <p className="font-semibold text-[#1A1D20]">{formatGender(listing.gender)}</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-4">
              <div className="flex items-center gap-2 text-[#5C636A] mb-1">
                <Shield className="w-4 h-4" />
                <span className="text-xs font-medium">Vaccination</span>
              </div>
              <p className="font-semibold text-[#1A1D20] capitalize">{listing.vaccination_status}</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-4">
              <div className="flex items-center gap-2 text-[#5C636A] mb-1">
                <Shield className="w-4 h-4" />
                <span className="text-xs font-medium">Hypoallergenic</span>
              </div>
              <p className="font-semibold text-[#1A1D20]">{formatHypoallergenic(listing.hypoallergenic)}</p>
            </div>
          </div>

          <div>
            <h3 className="font-['Nunito'] font-bold text-lg text-[#1A1D20] mb-2">Description</h3>
            <p className="text-[#5C636A] leading-relaxed whitespace-pre-line">{listing.description}</p>
          </div>

          {listing.breeder_info && (
            <div>
              <h3 className="font-['Nunito'] font-bold text-lg text-[#1A1D20] mb-2">Breeder Info</h3>
              <p className="text-[#5C636A] leading-relaxed">{listing.breeder_info}</p>
            </div>
          )}

          {externalHref && (
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
              <p className="text-xs font-bold uppercase tracking-wide text-[#4A90E2] mb-2">Listing source</p>
              <p className="text-sm text-[#5C636A] mb-4">
                This post was imported from {sourceLabel}. Open the original post to confirm the latest seller details and availability.
              </p>
              <Button
                asChild
                variant="outline"
                className="rounded-full border-[#4A90E2] text-[#4A90E2] hover:bg-[#4A90E2] hover:text-white font-bold"
                data-testid="view-original-listing-button"
              >
                <a href={externalHref} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" /> View on {sourceLabel}
                </a>
              </Button>
            </div>
          )}

          {/* Contact */}
          <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#4A90E2] flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-[#1A1D20]">{listing.user_name}</p>
                <p className="text-xs text-[#5C636A]">Posted {new Date(listing.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="space-y-3">
              {emailHref ? (
                <Button
                  asChild
                  className="w-full rounded-full bg-[#F5A623] hover:bg-[#e09515] text-white font-bold h-12 shadow-[0_4px_14px_rgb(245,166,35,0.4)] cta-button"
                  data-testid="contact-seller-button"
                >
                  <a href={emailHref}>
                    <Mail className="w-4 h-4 mr-2" /> Email Seller
                  </a>
                </Button>
              ) : (
                <Button disabled className="w-full rounded-full h-12 font-bold" data-testid="contact-seller-button">
                  <Mail className="w-4 h-4 mr-2" /> Email Not Available
                </Button>
              )}

              {phoneHref ? (
                <Button
                  asChild
                  variant="outline"
                  className="w-full rounded-full h-12 font-bold border-[#4A90E2] text-[#4A90E2] hover:bg-blue-50"
                  data-testid="call-seller-button"
                >
                  <a href={phoneHref}>
                    <Phone className="w-4 h-4 mr-2" /> Call Seller
                  </a>
                </Button>
              ) : (
                <Button disabled variant="outline" className="w-full rounded-full h-12 font-bold" data-testid="call-seller-button">
                  <Phone className="w-4 h-4 mr-2" /> Phone Not Available
                </Button>
              )}
            </div>
            {(email || phone) && (
              <div className="mt-4 space-y-1 text-xs text-[#5C636A]">
                {email && <p>Email opens pre-addressed to <span className="font-semibold text-[#1A1D20]">{email}</span>.</p>}
                {phone && <p>Phone: <span className="font-semibold text-[#1A1D20]">{phone}</span></p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
